import React, { useEffect, useState } from "react";
import { Box, Typography, Chip, Button, Alert, CircularProgress, Avatar } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import { useImpersonation } from "../../contexts/ImpersonationContext";

export default function ImpersonationAuditList() {
  const { listAudit } = useImpersonation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sesiones, setSesiones] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listAudit();
        if (!cancelled) setSesiones(data?.sesiones || []);
      } catch (e) {
        if (!cancelled) setError(e.status === 403 ? "Solo superadmins" : `Error: ${e.message}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listAudit]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          display: "grid", placeItems: "center", color: "#fff",
        }}>
          <HistoryIcon />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
            Auditoría de impersonation
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
            Sesiones de tu empresa · últimas 100
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="warning">{error}</Alert>
      ) : sesiones.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "#64748b" }}>
          Aún no hay sesiones de impersonation registradas.
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1.2 }}>
          {sesiones.map((s) => <SessionCard key={s.session_id} s={s} navigate={navigate} />)}
        </Box>
      )}
    </Box>
  );
}

function SessionCard({ s, navigate }) {
  const orig = name(s.original_user);
  const tgt = name(s.acting_as_user);
  const dur = duration(s.started_at, s.ended_at);
  const active = !s.ended_at;
  return (
    <Box sx={{
      p: 2, borderRadius: 2, bgcolor: "#fff",
      border: "1px solid #e2e8f0",
      display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
    }}>
      <Avatar sx={{ bgcolor: "#1e3a5f1a", color: "#1e3a5f" }}>{orig[0]?.toUpperCase()}</Avatar>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          #{s.session_id} · {orig} → {tgt}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#64748b" }}>
          {new Date(s.started_at).toLocaleString()} · duración {dur} · {s.acciones_total} acciones
          {s.acciones_criticas > 0 ? ` (${s.acciones_criticas} críticas)` : ""}
        </Typography>
        <Box sx={{ mt: 0.6, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          <Chip size="small" label={`Motivo: ${s.motivo}`} />
          {active ? (
            <Chip size="small" color="warning" label="ACTIVA" />
          ) : (
            <Chip size="small" label={s.end_reason || "cerrada"} />
          )}
          {s.acciones_criticas > 0 && (
            <Chip size="small" color="error" label={`${s.acciones_criticas} críticas`} variant="outlined" />
          )}
        </Box>
      </Box>
      <Button
        variant="outlined"
        onClick={() => navigate(`/admin/impersonate/audit/${s.session_id}`)}
        sx={{ textTransform: "none", fontWeight: 600 }}
      >
        Ver eventos
      </Button>
    </Box>
  );
}

function name(u) {
  if (!u) return "—";
  return `${u.name || ""} ${u.lastname || ""}`.trim() || u.email || `#${u.id}`;
}

function duration(start, end) {
  if (!start) return "—";
  const e = end ? new Date(end) : new Date();
  const ms = e - new Date(start);
  if (ms <= 0) return "—";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}
